import React, { createContext, useContext, useState, useEffect } from 'react';
import { manufacturingAPI } from '../services/api';

const ManufacturingDataContext = createContext();

export const useManufacturingData = () => {
  const context = useContext(ManufacturingDataContext);
  if (!context) {
    throw new Error('useManufacturingData must be used within ManufacturingDataProvider');
  }
  return context;
};

export const ManufacturingDataProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [operations, setOperations] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [lastItemsLoad, setLastItemsLoad] = useState(null);
  const [lastOperationsLoad, setLastOperationsLoad] = useState(null);

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadItems = async (force = false) => {
    const now = Date.now();
    if (!force && items.length > 0 && lastItemsLoad && (now - lastItemsLoad) < CACHE_DURATION) {
      return items; // Return cached data
    }

    setItemsLoading(true);
    try {
      const response = await manufacturingAPI.items.list({ page_size: 1000 });
      const itemsData = response.data.results || response.data || [];
      setItems(itemsData);
      setLastItemsLoad(now);
      return itemsData;
    } catch (error) {
      console.error('Error loading items:', error);
      return [];
    } finally {
      setItemsLoading(false);
    }
  };

  const loadOperations = async (force = false) => {
    const now = Date.now();
    if (!force && operations.length > 0 && lastOperationsLoad && (now - lastOperationsLoad) < CACHE_DURATION) {
      return operations; // Return cached data
    }

    setOperationsLoading(true);
    try {
      const response = await manufacturingAPI.operations.list();
      const operationsData = response.data.results || response.data || [];
      setOperations(operationsData);
      setLastOperationsLoad(now);
      return operationsData;
    } catch (error) {
      console.error('Error loading operations:', error);
      return [];
    } finally {
      setOperationsLoading(false);
    }
  };

  const refreshItems = () => loadItems(true);
  const refreshOperations = () => loadOperations(true);

  return (
    <ManufacturingDataContext.Provider value={{
      items,
      operations,
      itemsLoading,
      operationsLoading,
      loadItems,
      loadOperations,
      refreshItems,
      refreshOperations
    }}>
      {children}
    </ManufacturingDataContext.Provider>
  );
};